import React, { useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { UserSummaryType } from '../../../../../../../api/types/user/user-summary.type';
import { reduceInstallmentPlanTotalPriceOperation } from '../../operations/reduce-installment-plan-total-price-operation';

interface ReduceInstallmentPlanTotalPriceDialogProps {
  visible: boolean;
  user: UserSummaryType;
  installmentPlanId: number;
  onHide: () => void;
}

export const ReduceInstallmentPlanTotalPriceDialog: React.FC<
  ReduceInstallmentPlanTotalPriceDialogProps
> = ({ visible, user, installmentPlanId, onHide }) => {
  const [reduceAmount, setReduceAmount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const isValid = useMemo(() => {
    return (
      reduceAmount !== null &&
      Number.isInteger(reduceAmount) &&
      reduceAmount > 0
    );
  }, [reduceAmount]);

  const handleClose = () => {
    if (loading) return;
    setReduceAmount(null);
    onHide();
  };

  const handleApprove = async () => {
    if (!isValid || reduceAmount === null) return;

    await reduceInstallmentPlanTotalPriceOperation(
      user,
      installmentPlanId,
      reduceAmount,
      setLoading,
      handleClose,
    );
  };

  return (
    <Dialog
      header="הפחתת סכום כולל בהוראת קבע"
      visible={visible}
      onHide={handleClose}
      style={{ width: '520px', direction: 'rtl' }}
      modal
      closable={!loading}
      footer={
        <div
          style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}
        >
          <Button
            label="ביטול"
            icon="pi pi-times"
            severity="secondary"
            text
            onClick={handleClose}
            disabled={loading}
          />
          <Button
            label="אישור הפחתה"
            icon="pi pi-check"
            severity="warning"
            onClick={handleApprove}
            disabled={!isValid || loading}
            loading={loading}
          />
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            padding: '14px 16px',
            backgroundColor: '#FEF3C7',
            borderRadius: '10px',
            border: '1px solid #F59E0B',
          }}
        >
          <i
            className="pi pi-exclamation-triangle"
            style={{ fontSize: '22px', color: '#F59E0B', marginTop: '2px' }}
          />
          <div style={{ color: '#92400E' }}>
            <div style={{ fontWeight: 700, marginBottom: '4px' }}>
              חשוב לדעת לפני שמפחיתים סכום
            </div>
            <div style={{ fontSize: '14px', lineHeight: 1.5 }}>
              הוראת הקבע הזו מקושרת לשירותי תשלום. הפחתה של הסכום הכולל תתעדכן
              ותופחת <b>בכל</b> שירותי התשלום המקושרים להוראת הקבע הזו באופן
              שווה.
            </div>
          </div>
        </div>

        <div
          style={{
            padding: '16px',
            backgroundColor: '#F3F4F6',
            borderRadius: '10px',
          }}
        >
          <label
            htmlFor="reduceAmount"
            style={{
              display: 'block',
              marginBottom: '10px',
              fontWeight: 700,
              color: '#374151',
            }}
          >
            סכום להפחתה (מספר שלם) *
          </label>
          <InputNumber
            id="reduceAmount"
            value={reduceAmount}
            onValueChange={(e) => setReduceAmount(e.value)}
            min={1}
            maxFractionDigits={0}
            useGrouping
            style={{ width: '100%', direction: 'ltr' }}
            inputStyle={{ direction: 'rtl', textAlign: 'right' }}
            disabled={loading}
            placeholder="הזן סכום להפחתה"
          />
          <div
            style={{ marginTop: '10px', fontSize: '13px', color: '#6B7280' }}
          >
            טיפ: מומלץ להזין סכום קטן יותר מהיתרה הכוללת של הוראת הקבע.
          </div>
        </div>

        <div
          style={{
            padding: '12px',
            backgroundColor: '#FEF2F2',
            borderRadius: '10px',
            border: '1px solid #FECACA',
            fontSize: '14px',
            color: '#991B1B',
          }}
        >
          <i className="pi pi-info-circle" style={{ marginLeft: '8px' }} />
          פעולה זו תשפיע על כל שירותי התשלום המקושרים להוראת הקבע.
        </div>
      </div>
    </Dialog>
  );
};
